import { useLocation, BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { WSProvider } from "@/context/WSContext";
import { Toaster } from "sonner";

import ErrorBoundary from "@/components/ErrorBoundary";
import Sidebar from "@/components/Sidebar";
import BottomNav from "@/components/BottomNav";
import TipNotifier from "@/components/TipNotifier";
import WebPushBridge from "@/components/WebPushBridge";
import GiftShowcase from "@/components/GiftShowcase";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import PeakTimeReminder from "@/components/PeakTimeReminder";
import SharerRewardNotifier from "@/components/SharerRewardNotifier";

import Feed from "@/pages/Feed";
import Login from "@/pages/Login";
import Upload from "@/pages/Upload";
import Profile from "@/pages/Profile";
import Search from "@/pages/Search";
import Discover from "@/pages/Discover";
import VideoView from "@/pages/VideoView";
import AuthCallback from "@/pages/AuthCallback";
import GenerateVideo from "@/pages/GenerateVideo";
import Messages from "@/pages/Messages";
import Conversation from "@/pages/Conversation";
import Notifications from "@/pages/Notifications";
import HashtagPage from "@/pages/HashtagPage";
import Earnings from "@/pages/Earnings";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminAnalytics from "@/pages/AdminAnalytics";
import MyBoosts from "@/pages/MyBoosts";
import Wallet from "@/pages/Wallet";
import Leaderboard from "@/pages/Leaderboard";
import SoundDetail from "@/pages/SoundDetail";
import Pro from "@/pages/Pro";
import ProSuccess from "@/pages/ProSuccess";
import ProMembers from "@/pages/ProMembers";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ShopAnalytics from "@/pages/ShopAnalytics";
import TopSharers from "@/pages/TopSharers";
import MySignups from "@/pages/MySignups";
import UserShareAdmin from "@/pages/UserShareAdmin";
import UserSignupsAdmin from "@/pages/UserSignupsAdmin";
import GiftEditorAdmin from "@/pages/GiftEditorAdmin";
import GuideAgentAdmin from "@/pages/GuideAgentAdmin";
import LiveGrid from "@/pages/LiveGrid";
import LiveRoom from "@/pages/LiveRoom";

import LevelUpTakeover from "@/components/LevelUpTakeover";

function Shell() {
  const location = useLocation();
  // CRITICAL: detect OAuth callback synchronously to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isFeed = location.pathname === "/" || /^\/v\//.test(location.pathname);
  // Live rooms take over the entire viewport — hiding the sidebar +
  // bottom nav is what makes chat + gift buttons reachable on mobile.
  const isLiveRoom = /^\/live\/[^/]+/.test(location.pathname);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <ImpersonationBanner />
      {!isLiveRoom && <Sidebar />}
      {!isLiveRoom && <BottomNav />}
      <main className={isFeed ? "md:ml-60" : ""}>
        <Routes>
          <Route path="/" element={<Feed />} />
          <Route path="/login" element={<Login />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/generate" element={<GenerateVideo />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/messages/:conversationId" element={<Conversation />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/t/:tag" element={<HashtagPage />} />
          <Route path="/earnings" element={<Earnings />} />
          <Route path="/shop-analytics" element={<ShopAnalytics />} />
          <Route path="/top-sharers" element={<TopSharers />} />
          <Route path="/my-signups" element={<MySignups />} />
          <Route path="/admin/user/:username/shares" element={<UserShareAdmin />} />
          <Route path="/admin/user/:username/signups" element={<UserSignupsAdmin />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/my-boosts" element={<MyBoosts />} />
          <Route path="/admin/payouts" element={<AdminPayouts />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/admin/gifts" element={<GiftEditorAdmin />} />
          <Route path="/admin/guide-agent" element={<GuideAgentAdmin />} />
          <Route path="/search" element={<Search />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/u/:username" element={<Profile />} />
          <Route path="/v/:id" element={<VideoView />} />
          <Route path="/sound/:trackId" element={<SoundDetail />} />
          <Route path="/pro" element={<Pro />} />
          <Route path="/pro/success" element={<ProSuccess />} />
          <Route path="/pro/members" element={<ProMembers />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/live" element={<LiveGrid />} />
          <Route path="/live/:roomName" element={<LiveRoom />} />
        </Routes>
      </main>
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0A0A0C",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#fff",
            fontFamily: "JetBrains Mono, monospace",
            fontSize: "12px",
            letterSpacing: "0.04em",
          },
        }}
      />
      <TipNotifier />
      <WebPushBridge />
      <GiftShowcase />
      <LevelUpTakeover />
      <PeakTimeReminder />
      <SharerRewardNotifier />
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <WSProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </WSProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
